'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import AffiliateLink from '@/components/common/AffiliateLink'
import { t } from '@/i18n/translations'

/**
 * Generic "Gallery · Field" comparison ledger (operator/type chips + optional
 * scope toggle + sortable columns), shared by the eSIM-style service pages
 * (VPN, Insurance, Transport routes, Tours, Stay). Mirrors EsimPlansLedger but
 * is driven by a serializable config so each page only supplies data + columns.
 *
 * Props:
 *  - ops:      [{ id:'all', name }, { id, name }, …] — вкладки-оператор (tabs)
 *  - data:     { local: { opId:[row] }, regional: { opId:[row] } }  (regional optional)
 *  - columns:  [{ key, label, fmt, sortable, suffix, opTag, rec, opTagFilterKey, filterKey, filterValueKey }]
 *              fmt ∈ text | devices | price | priceInt | coverage | duration | time | optype
 *              opTagFilterKey/filterKey — клик по значению включает фильтр с этим ключом
 *  - scope:    { local, regional } toggle labels, or null for no toggle
 *  - note:     { local, regional } caption strings
 *  - filters:  [{ key, label, allLabel, type, options }] — типы:
 *              'select' (нативный, по умолчанию) | 'choice' (один из чипов) |
 *              'multi' (кастомный мультивыбор) | 'chips' (мультивыбор чипами,
 *              у опции может быть icon) | 'toggle' (бейдж вкл/выкл)
 *  - scopeInFilters: показать scope toggle как первую группу фильтров
 *  - scopeLabel: подпись scope-фильтра
 *  - collapsibleFilters: спрятать фильтры под спойлер (как в eSIM)
 *  - initialLimit: показать первые N строк + кнопку «Показать ещё» (опционально)
 *  - priorityKey / priorityValues: значения, которые всегда идут первыми при сортировке
 *  - gridClass / selectLabel / selectHref / recLabel / defaultSort
 * Each row is a plain object carrying the column fields + `price` (number),
 * `rec` (bool) and `opName` (brand/type label for the op-tag / optype column).
 * Optional row extras: `_cur` (валюта цены), `_icon` (React-компонент иконки
 * перед названием), `selHref`/`_ext` (внешняя ссылка), `_selLabel`
 * (подпись кнопки).
 */

// Кастомный мультивыбор (чекбоксы в дропдауне) — нативный <select multiple>
// неудобен на тач-устройствах и не стилизуется.
function MultiSelect({ allLabel, options, selected, onToggle }) {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)
    useEffect(() => {
        if (!open) return undefined
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [open])

    const count = selected.length
    const summary = count === 0
        ? allLabel
        : (count === 1 ? (options.find((o) => o.value === selected[0])?.label || allLabel) : `${allLabel}: ${count}`)

    return (
        <div className={`gf-ms ${open ? 'open' : ''}`} ref={ref}>
            <button type="button" className={`gf-ms-btn ${count ? 'on' : ''}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
                <span className="gf-ms-text">{summary}</span>
                <span className="gf-ms-caret" aria-hidden>▾</span>
            </button>
            {open && (
                <div className="gf-ms-menu" role="listbox" aria-multiselectable="true">
                    {options.map((o) => (
                        <label key={o.value} className="gf-ms-opt">
                            <input type="checkbox" checked={selected.includes(o.value)} onChange={() => onToggle(o.value)} />
                            <span>{o.label}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    )
}

export default function GfServiceLedger({
    lang = 'ru', ops = [], data = {}, columns = [], scope = null, note = {},
    filters = null, collapsibleFilters = false, initialLimit = 0,
    scopeInFilters = false, scopeLabel,
    priorityKey, priorityValues = [],
    gridClass = '', selectLabel, selectHref = '#', recLabel, defaultSort,
}) {
    const ui = t(lang, 'serviceWidgetsUi').ledger
    const [op, setOp] = useState('all')
    const [scp, setScp] = useState('local')
    const [sortKey, setSortKey] = useState(defaultSort || columns.find((c) => c.sortable)?.key)
    const [sortDir, setSortDir] = useState(1)
    const [filterVals, setFilterVals] = useState({})  // select→строка, chips/multi→массив, toggle→bool
    const [expanded, setExpanded] = useState(false)    // «Показать ещё» (строки)
    const [filtersOpen, setFiltersOpen] = useState(false) // спойлер фильтров
    const rec = recLabel || ui.rec
    const sel = selectLabel || ui.select
    const hasFilters = Array.isArray(filters) && filters.length > 0
    const filterList = useMemo(() => (hasFilters ? filters : []), [hasFilters, filters])
    const primaryFilters = filterList.filter((f) => f.pinned)
    const advancedFilters = filterList.filter((f) => !f.pinned)
    const showScopeAsFilter = Boolean(scope && scopeInFilters)
    const hasPrimaryFilters = primaryFilters.length > 0 || showScopeAsFilter
    const hasAdvancedFilters = advancedFilters.length > 0
    const hasFilterControls = hasPrimaryFilters || hasAdvancedFilters
    const scopeFilterLabel = scopeLabel || ui.scopeType
    const priorityList = useMemo(() => (Array.isArray(priorityValues) ? priorityValues : []), [priorityValues])

    // ── мутаторы фильтров ──
    const setSelectFilter = (key, value) => { setFilterVals((v) => ({ ...v, [key]: value })); setExpanded(false) }
    const toggleArrFilter = (key, value) => {
        setFilterVals((s) => {
            const arr = s[key] || []
            return { ...s, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] }
        })
        setExpanded(false)
    }
    const toggleBoolFilter = (key) => { setFilterVals((s) => ({ ...s, [key]: !s[key] })); setExpanded(false) }
    const resetFilters = () => {
        setFilterVals({})
        if (showScopeAsFilter) setScp('local')
        setExpanded(false)
    }

    // Клик по значению в строке (категория/город) → включить соответствующий фильтр
    const applyRowFilter = (key, value) => {
        const f = filterList.find((x) => x.key === key)
        if (!f || value == null) return
        if (!f.pinned) setFiltersOpen(true)
        if (f.type === 'select' || f.type === 'choice') setSelectFilter(key, filterVals[key] === value ? 'all' : value)
        else toggleArrFilter(key, value)
    }

    const toggleOp = (id) => { setOp(id); setExpanded(false) }

    const rows = useMemo(() => {
        const getPriority = (row) => {
            if (!priorityKey || priorityList.length === 0) return 0
            const idx = priorityList.indexOf(row?.[priorityKey])
            return idx === -1 ? priorityList.length : idx
        }
        const activeData = scope ? (data[scp] || {}) : (data.local || data || {})
        const ids = op === 'all' ? Object.keys(activeData) : [op]
        let list = []
        ids.forEach((id) => (activeData[id] || []).forEach((r) => list.push(r)))
        for (const f of filterList) {
            const v = filterVals[f.key]
            if (f.type === 'toggle') {
                if (v) list = list.filter((r) => r[f.key])
            } else if (f.type === 'multi' || f.type === 'chips') {
                const arr = v || []
                if (arr.length) list = list.filter((r) => {
                    const rv = r[f.key]
                    return Array.isArray(rv) ? rv.some((x) => arr.includes(x)) : arr.includes(rv)
                })
            } else if (v && v !== 'all') {
                list = list.filter((r) => (Array.isArray(r[f.key]) ? r[f.key].includes(v) : r[f.key] === v))
            }
        }
        if (!sortKey) return list
        return list.slice().sort((a, b) => {
            const pr = getPriority(a) - getPriority(b)
            if (pr) return pr
            const x = a[sortKey]
            const y = b[sortKey]
            if (x === y) return 0
            if (typeof x === 'string') return x < y ? -sortDir : sortDir
            return (x < y ? -1 : 1) * sortDir
        })
    }, [op, scp, sortKey, sortDir, data, scope, filterList, filterVals, priorityKey, priorityList])

    const visibleRows = initialLimit > 0 && !expanded ? rows.slice(0, initialLimit) : rows
    const hiddenCount = rows.length - visibleRows.length

    const total = useMemo(() => {
        const activeData = scope ? (data[scp] || {}) : (data.local || data || {})
        return Object.values(activeData).reduce((n, arr) => n + (Array.isArray(arr) ? arr.length : 0), 0)
    }, [data, scope, scp])

    // Сводка активных фильтров для шапки спойлера
    const getFilterParts = (items) => {
        const parts = []
        for (const f of items) {
            const v = filterVals[f.key]
            if (f.type === 'toggle') { if (v) parts.push(f.label) }
            else if (f.type === 'multi' || f.type === 'chips') {
                const arr = v || []
                if (arr.length) parts.push(`${f.label}: ${arr.map((val) => f.options.find((o) => o.value === val)?.label || val).join(', ')}`)
            } else if (v && v !== 'all') {
                parts.push(`${f.label ? `${f.label}: ` : ''}${f.options.find((o) => o.value === v)?.label || v}`)
            }
        }
        return parts
    }
    const primaryParts = getFilterParts(primaryFilters)
    const advancedParts = getFilterParts(advancedFilters)
    const hasActive = primaryParts.length > 0 || advancedParts.length > 0 || (showScopeAsFilter && scp !== 'local')
    // Сводка в шапке спойлера: активные значения, иначе — подписи реально доступных фильтров
    const filterSummary = advancedParts.length
        ? advancedParts.join(' · ')
        : advancedFilters.map((f) => f.label).filter(Boolean).join(' · ')

    const toggleSort = (k) => {
        if (sortKey === k) setSortDir((d) => d * -1)
        else { setSortKey(k); setSortDir(1) }
    }

    const fmtCell = (row, c) => {
        const v = row[c.key]
        switch (c.fmt) {
            case 'devices': return v >= 999 ? '∞' : String(v)
            case 'price': return `${row._cur || '€'}${Number(v).toFixed(2)}${c.suffix ? ` ${c.suffix}` : ''}`
            case 'priceInt': return `${row._cur || '€'}${Math.round(Number(v))}${c.suffix ? ` ${c.suffix}` : ''}`
            case 'coverage': return `€${v} 000`
            case 'duration': return `${v} ${ui.hourShort}`
            case 'optype': return row.opName
            case 'tier': {
                const r = Math.max(0, Math.min(3, Math.round(Number(v))))
                return '€'.repeat(r + 1)
            }
            case 'time': {
                if (row._timeLabel) return row._timeLabel
                if (typeof v !== 'number') return v
                if (v < 60) return `${v} ${ui.minShort}`
                const h = Math.floor(v / 60)
                const m = v % 60
                return m ? `${h} ${ui.hourShort} ${m} ${ui.minShort}` : `${h} ${ui.hourShort}`
            }
            default: return v
        }
    }

    const showOpTag = op === 'all'

    // ── рендер групп фильтров (используется и в спойлере, и инлайн) ──
    const renderScopeFilterGroup = () => {
        if (!showScopeAsFilter) return null
        return (
            <div className="gf-fg gf-fg-scope" key="__scope">
                <span className="gf-fl">{scopeFilterLabel}</span>
                <div className="gf-chips">
                    <button type="button" className={scp === 'local' ? 'on' : ''} onClick={() => { setScp('local'); setExpanded(false) }}>{scope.local}</button>
                    <button type="button" className={scp === 'regional' ? 'on' : ''} onClick={() => { setScp('regional'); setExpanded(false) }}>{scope.regional}</button>
                </div>
            </div>
        )
    }

    const renderFilterGroups = (items, { includeScope = false } = {}) => (
        <>
            {includeScope && renderScopeFilterGroup()}
            {items.map((f) => {
                if (f.type === 'choice') {
                    const value = filterVals[f.key] || 'all'
                    return (
                        <div className="gf-fg gf-fg-chips" key={f.key}>
                            {f.label && <span className="gf-fl">{f.label}</span>}
                            <div className="gf-chips">
                                {f.options.map((o) => (
                                    <button type="button" key={o.value} className={value === o.value ? 'on' : ''} onClick={() => setSelectFilter(f.key, value === o.value ? 'all' : o.value)}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )
                }
                if (f.type === 'chips') {
                    const seld = filterVals[f.key] || []
                    return (
                        <div className="gf-fg gf-fg-chips" key={f.key}>
                            {f.label && <span className="gf-fl">{f.label}</span>}
                            <div className="gf-chips">
                                {f.options.map((o) => {
                                    const Icon = o.icon
                                    return (
                                        <button type="button" key={o.value} className={seld.includes(o.value) ? 'on' : ''} onClick={() => toggleArrFilter(f.key, o.value)}>
                                            {Icon && <Icon aria-hidden />}{o.label}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    )
                }
                if (f.type === 'toggle') {
                    return (
                        <div className="gf-fg gf-fg-toggle" key={f.key}>
                            <div className="gf-chips">
                                <button type="button" className={filterVals[f.key] ? 'on' : ''} onClick={() => toggleBoolFilter(f.key)}>{f.label}</button>
                            </div>
                        </div>
                    )
                }
                if (f.type === 'multi') {
                    return (
                        <div className="gf-fg gf-fg-multi" key={f.key}>
                            {f.label && <span className="gf-fl">{f.label}</span>}
                            <MultiSelect allLabel={f.allLabel} options={f.options} selected={filterVals[f.key] || []} onToggle={(v) => toggleArrFilter(f.key, v)} />
                        </div>
                    )
                }
                return (
                    <div className="gf-fg gf-fg-select" key={f.key}>
                        {f.label && <span className="gf-fl">{f.label}</span>}
                        <select className="hf-sel" value={filterVals[f.key] || 'all'} onChange={(e) => setSelectFilter(f.key, e.target.value)} aria-label={f.allLabel}>
                            <option value="all">{f.allLabel}</option>
                            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                )
            })}
        </>
    )

    return (
        <>
            {ops.length > 0 && (
                <div className="gf-ops" role="group" aria-label={ui.filterAria}>
                    {ops.map((o) => (
                        <button type="button" key={o.id} className={`${o.id === 'all' ? 'all ' : ''}${op === o.id ? 'on' : ''}`} onClick={() => toggleOp(o.id)}>{o.name}</button>
                    ))}
                </div>
            )}

            {hasPrimaryFilters && (
                <div className="gf-ledger-filters gf-ledger-filter-grid gf-ledger-filter-primary">
                    {renderFilterGroups(primaryFilters, { includeScope: showScopeAsFilter })}
                </div>
            )}

            {hasAdvancedFilters && collapsibleFilters && (
                <section className={`gf-filter-spoiler ${filtersOpen ? 'open' : ''}`} aria-label={ui.filtersAria}>
                    <button type="button" className="gf-filter-toggle" aria-expanded={filtersOpen} onClick={() => setFiltersOpen((o) => !o)}>
                        <span className="gf-filter-copy">
                            <span className="gf-filter-title">{ui.moreFilters}</span>
                            <span className="gf-filter-state">{filterSummary}</span>
                        </span>
                        <span className="gf-filter-icon" aria-hidden>{filtersOpen ? '−' : '+'}</span>
                    </button>
                    {filtersOpen && <div className="gf-filters gf-ledger-filter-grid">{renderFilterGroups(advancedFilters)}</div>}
                </section>
            )}

            {hasAdvancedFilters && !collapsibleFilters && (
                <div className="gf-ledger-filters gf-ledger-filter-grid">{renderFilterGroups(advancedFilters)}</div>
            )}

            {hasFilterControls && (
                <div className="gf-ledger-countbar">
                    <span className="gf-plan-count">{rows.length} {ui.of} {total}</span>
                    {showScopeAsFilter && <span className="gf-note">{scp === 'regional' ? note.regional : note.local}</span>}
                    {hasActive && <button type="button" className="gf-filter-reset" onClick={resetFilters}>{ui.resetFilters}</button>}
                </div>
            )}

            {scope && !showScopeAsFilter && (
                <div className="gf-plan-head">
                    <div className="gf-toggle">
                        <button type="button" className={scp === 'local' ? 'on' : ''} onClick={() => { setScp('local'); setExpanded(false) }}>{scope.local}</button>
                        <button type="button" className={scp === 'regional' ? 'on' : ''} onClick={() => { setScp('regional'); setExpanded(false) }}>{scope.regional}</button>
                    </div>
                    <span className="gf-note">{scp === 'regional' ? note.regional : note.local}</span>
                </div>
            )}

            <div className={`gf-plans ${gridClass}`}>
                <div className="gf-plan-row head">
                    {columns.map((c) => (c.sortable ? (
                        <span key={c.key} data-sort={c.key} className={sortKey === c.key ? 'act' : ''} role="button" tabIndex={0}
                            onClick={() => toggleSort(c.key)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(c.key) } }}>
                            {c.label}<i className="ar">{sortKey === c.key ? (sortDir > 0 ? '▲' : '▼') : ''}</i>
                        </span>
                    ) : (
                        <span key={c.key}>{c.label}</span>
                    )))}
                    {/* invisible clone of the Select link so the header's last (auto)
                        track reserves the same width as the data rows — keeps the
                        fr columns lined up under their headings (each row is its own grid). */}
                    <span className="sel" aria-hidden="true" style={{ visibility: 'hidden' }}>{sel}</span>
                </div>
                {visibleRows.map((row, i) => {
                    const RowIcon = row._icon || null
                    return (
                        <div className={`gf-plan-row ${row.rec ? 'rec' : ''}`} key={i}>
                            {columns.map((c, ci) => (
                                <span key={c.key} className={ci === 0 ? 'd' : (c.fmt === 'price' || c.fmt === 'priceInt' || c.fmt === 'tier') ? 'p' : 'v'}>
                                    {ci === 0 ? (
                                        <>
                                            {showOpTag && c.opTag && (
                                                c.opTagFilterKey
                                                    ? <button type="button" className="op-tag op-tag-btn" onClick={() => applyRowFilter(c.opTagFilterKey, row._cat)}>{row.opName}</button>
                                                    : <span className="op-tag">{row.opName}</span>
                                            )}
                                            <span className="gbline">
                                                {RowIcon && <i className="lg-ic" aria-hidden><RowIcon /></i>}
                                                <span className="gb-copy">
                                                    <span className="gb-title">{row.nameHref ? <a href={row.nameHref}>{fmtCell(row, c)}</a> : fmtCell(row, c)}</span>
                                                    {row.rec && c.rec && <span className="recl">{rec}</span>}
                                                </span>
                                            </span>
                                        </>
                                    ) : (
                                        c.filterKey
                                            ? <button type="button" className="cell-filter-btn" onClick={() => applyRowFilter(c.filterKey, row[c.filterValueKey || c.key])}>{fmtCell(row, c)}</button>
                                            : fmtCell(row, c)
                                    )}
                                </span>
                            ))}
                            {/* _ext — строка ведёт на внешнюю (партнёрскую) ссылку, а не на якорь страницы */}
                            {row._ext
                                ? <AffiliateLink className="sel" href={row.selHref || selectHref} provider={row._src} position="ledger">{row._selLabel || sel}</AffiliateLink>
                                : <a className="sel" href={row.selHref || selectHref}>{row._selLabel || sel}</a>}
                        </div>
                    )
                })}
            </div>

            {hiddenCount > 0 && (
                <div className="gf-more-wrap">
                    <button type="button" className="gf-more" onClick={() => setExpanded(true)}>
                        {ui.showMore(hiddenCount)}
                    </button>
                </div>
            )}
        </>
    )
}
